'use client';
import { Button } from '@ahafood/ui/components/button';
import { Card } from '@ahafood/ui/components/card';
import ShimmerCard from '@ahafood/ui/components/shimmer-card';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { ArrowRight } from 'lucide-react';
import Image from 'next/image';
import React from 'react';
import { trackEvent } from '@/lib/event-tracking';
import { useAuthStore } from '@/store';

type AmoriProps = {
  className?: string;
  onSuggestionClick?: (suggestion: string) => void;
};

export default function AmoriBanner({
  className,
  onSuggestionClick,
}: AmoriProps) {
  const { user } = useAuthStore();

  return (
    <section
      className={cn(
        'mx-auto max-w-screen-xl bg-white p-4 pt-2 md:py-8 [&+section.ahf-deal]:-mt-10 md:[&+section.ahf-deal]:mt-0 md:hidden w-full',
        className
      )}
    >
      <ShimmerCard>
        <Card
          className="rounded-xl p-2 flex items-center bg-bottom bg-no-repeat bg-contain justify-between overflow-hidden border-none relative"
          style={{
            backgroundImage: `url(${assetPrefix}/static/images/bg-amori-banner.webp)`,
          }}
          onClick={() => {
            if (
              typeof window !== 'undefined' &&
              typeof window.setOpenChange === 'function'
            ) {
              window.setOpenChange(true);
              trackEvent('chatbot_event', {
                button_name: 'chatbot_banner',
                timestamp: Date.now(),
                user_id: user?.cunstomerId,
              });
            }
          }}
        >
          <div className="bg-gradient-to-r from-red-50  to-blue-50 bg-clip-text text-transparent">
            <div className="-ml-2">
              <Image
                src={`${assetPrefix}/static/images/freeship.png`}
                alt="Chatbot Detail"
                width={127}
                height={20}
              />
            </div>
            <div className="p-2">
              <h3 className="font-bold text-lg flex">
                Một mình? Có Amori
                <div className="ml-2">
                  <Image
                    src={`${assetPrefix}/static/images/ai-icon.png`}
                    alt="Chatbot Detail"
                    width={33}
                    height={24}
                  />
                </div>
              </h3>
              <div className="flex items-center">
                <p className="text-sm font-semibold">Một bữa? Có </p>
                <Image
                  width={71}
                  height={21}
                  className="ml-1"
                  src={`${assetPrefix}/static/images/solo-meal.png`}
                  alt={'Solo Meal'}
                />
              </div>
            </div>
            <div className="w-2/3 flex gap-3 text-neutral-90">
              <Button
                className="bg-white/30 border-2 border-white w-full px-2 py-1 rounded-lg hover:bg-white/50 font-light text-neutral-90 z-20 h-8"
                onClick={(e) => {
                  e.stopPropagation();
                  // Lưu suggestion để chatbot tự động gửi
                  if (typeof window !== 'undefined') {
                    sessionStorage.setItem(
                      'ahafood.chat.autoSuggestion',
                      'Solo meal'
                    );
                  }
                  onSuggestionClick?.('Solo meal');
                  // Mở chatbot
                  if (
                    typeof window !== 'undefined' &&
                    typeof window.setOpenChange === 'function'
                  ) {
                    window.setOpenChange(true);
                    trackEvent('chatbot_event', {
                      button_name: 'chatbot_banner_solo_meal',
                      timestamp: Date.now(),
                      user_id: user?.cunstomerId,
                    });
                  }
                }}
              >
                Solo meal 🔥
              </Button>
              <Button
                className="bg-white/30 border-2 border-white w-full px-2 py-1 rounded-lg hover:bg-white/50 font-light text-neutral-90 z-20 h-8"
                onClick={(e) => {
                  e.stopPropagation();
                  // Lưu suggestion để chatbot tự động gửi
                  if (typeof window !== 'undefined') {
                    sessionStorage.setItem(
                      'ahafood.chat.autoSuggestion',
                      'Hôm nay ăn gì?'
                    );
                  }
                  onSuggestionClick?.('Hôm nay ăn gì?');
                  // Mở chatbot
                  if (
                    typeof window !== 'undefined' &&
                    typeof window.setOpenChange === 'function'
                  ) {
                    window.setOpenChange(true);
                    trackEvent('chatbot_event', {
                      button_name: 'chatbot_banner_what_to_eat',
                      timestamp: Date.now(),
                      user_id: user?.cunstomerId,
                    });
                  }
                }}
              >
                Hôm nay ăn gì?
              </Button>
              <Button variant="ghost" className="[&_svg]:size-5 px-1 h-8">
                <ArrowRight />
              </Button>
            </div>
          </div>

          <div className="aspect-auto size-24 overflow-visible flex items-center justify-center">
            <Image
              src={`${assetPrefix}/static/images/chatbot-detail.webp`}
              alt="Amori Logo"
              width={128}
              height={128}
              className="object-cover object-top"
              style={{ objectPosition: 'top', objectFit: 'cover' }}
            />
          </div>
          <Image
            className="z-10 pl-[30%]"
            src={`${assetPrefix}/static/images/pattern-bg-amori.webp`}
            alt="Amori"
            fill
          />
        </Card>
      </ShimmerCard>
    </section>
  );
}
